import { CommunitySupportWidget } from "@/components/Analytics/CommunitySupportWidget";
import { FahWidget } from "@/components/Analytics/FahWidget";
import { PlatformReachWidget } from "@/components/Analytics/PlatformReachWidget";
import { PlatformResidentsWidget } from "@/components/Analytics/PlatformResidentsWidget";
import { TicketResolutionTimeWidget } from "@/components/Analytics/TicketResolutionTimeWidget";
import { TicketSatisfactionWidget } from "@/components/Analytics/TicketSatisfactionWidget";
import type { Period } from "@/components/Analytics/types";
import { PageGrid } from "@/components/PageGrid/PageGrid";
import { Stack } from "@/design-system/Stack";
import { useTranslation } from "@/translations";

import { AnalyticsSection } from "./AnalyticsSection";

interface AnalyticsOverviewSectionsProps {
  projectIds: string[];
  feelingAtHomeProjectIds: string[];
  period: Period;
}

/** A question-led overview composed entirely from existing analytics widgets. */
export function AnalyticsOverviewSections({
  projectIds,
  feelingAtHomeProjectIds,
  period,
}: AnalyticsOverviewSectionsProps): React.ReactNode {
  const { t } = useTranslation();

  return (
    <Stack gap="xl">
      <p className="max-w-measure-wide text-body text-text-secondary">{t("page.analytics-story.introduction")}</p>

      <PageGrid.Row>
        {feelingAtHomeProjectIds.length > 0 && (
          <PageGrid.Column size="100%">
            <AnalyticsSection
              isFullWidth
              title={t("page.analytics-story.feeling.title")}
              description={t("page.analytics-story.feeling.description")}
            >
              <FahWidget projectIds={feelingAtHomeProjectIds} period={period} />
            </AnalyticsSection>
          </PageGrid.Column>
        )}

        <PageGrid.Column size="100%">
          <AnalyticsSection
            title={t("page.analytics-story.participation.title")}
            description={t("page.analytics-story.participation.description")}
          >
            <PlatformReachWidget projectIds={projectIds} period={period} />
            <Stack gap="lg">
              <PlatformResidentsWidget projectIds={projectIds} type="engaging" period={period} />
              <PlatformResidentsWidget projectIds={projectIds} type="contributing" period={period} />
            </Stack>
          </AnalyticsSection>
        </PageGrid.Column>

        <PageGrid.Column size="100%">
          <AnalyticsSection
            title={t("page.analytics-story.self-reliance.title")}
            description={t("page.analytics-story.self-reliance.description")}
          >
            <CommunitySupportWidget projectIds={projectIds} period={period} />
            <PlatformResidentsWidget projectIds={projectIds} type="supportive" period={period} />
          </AnalyticsSection>
        </PageGrid.Column>

        <PageGrid.Column size="100%">
          <AnalyticsSection
            title={t("page.analytics-story.service.title")}
            description={t("page.analytics-story.service.description")}
          >
            <TicketSatisfactionWidget projectIds={projectIds} period={period} />
            <TicketResolutionTimeWidget projectIds={projectIds} period={period} />
          </AnalyticsSection>
        </PageGrid.Column>
      </PageGrid.Row>
    </Stack>
  );
}
